import { useTimerStore } from '@/store/useTimerStore';

interface RectangularTimerReadOnlyProps {
  timer: {
    id: number | string;
    title: string;
    segments: Array<{
      id: string;
      type: 'text' | 'image' | 'video';
      content: string;
      duration: number;
    }>;
    durationSeconds?: number;
    duration?: number;
  };
}

export default function RectangularTimerReadOnly({ timer }: RectangularTimerReadOnlyProps) {
  const totalDuration = timer.segments.reduce((sum, section) => sum + section.duration, 0) 
    || timer.durationSeconds 
    || timer.duration 
    || 0;
  
  // PRIMITIVE SELECTORS - each returns a primitive value, guaranteed stable
  // Only re-renders when actual value changes (primitives are reference-stable)
  const status = useTimerStore(state => state.timers[timer.id]?.status ?? 'idle');
  const timeRemaining = useTimerStore(state => state.timers[timer.id]?.timeRemaining ?? totalDuration);
  const isRunning = useTimerStore(state => state.timers[timer.id]?.isRunning ?? false);
  
  // 100% STORE-DRIVEN - no prop fallbacks for state
  const remainingSeconds = timeRemaining;
    
  // PURELY READ-ONLY COMPONENT
  // NO store writes here - all hydration happens at Care View level
  // Component only subscribes to store and displays values

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentRemaining = Math.max(0, Math.floor(remainingSeconds));

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 space-y-6" data-testid={`rectangular-timer-readonly-${timer.id}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900">{timer.title}</h3>
        <div className="flex items-center gap-3">
          <span className={`text-3xl font-bold ${isRunning ? 'text-orange-500' : 'text-gray-700'}`}>
            {formatTime(currentRemaining)}
          </span>
          {isRunning && (
            <span className="w-4 h-4 bg-orange-500 rounded-full animate-pulse"></span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-4">
        {timer.segments.map((section, index) => {
          let accumulatedTime = 0;
          let segmentProgress = 0;
          let isCompleted = false;
          let isCurrent = false;

          for (let i = 0; i < index; i++) {
            accumulatedTime += timer.segments[i].duration;
          }

          const elapsedTotal = totalDuration - currentRemaining;

          if (elapsedTotal > accumulatedTime + section.duration) {
            isCompleted = true;
            segmentProgress = 100;
          } else if (elapsedTotal > accumulatedTime) {
            isCurrent = true;
            const segmentElapsed = elapsedTotal - accumulatedTime;
            segmentProgress = (segmentElapsed / section.duration) * 100;
          }

          const segmentCount = timer.segments.length;
          const segmentWidth = segmentCount <= 2 ? 'w-40' : segmentCount <= 4 ? 'w-32' : 'w-28';

          return (
            <div key={index} className={`relative flex-shrink-0 ${segmentWidth}`}>
              <div className={`aspect-square relative overflow-hidden rounded-xl border-3 ${
                isCurrent ? 'border-orange-400 shadow-lg shadow-orange-200' : isCompleted ? 'border-green-400' : 'border-gray-300'
              }`}>
                {section.content && section.type === 'image' ? (
                  <img 
                    src={section.content} 
                    alt={`Section ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <span className="text-gray-600 text-base text-center p-3 font-medium">
                      {section.type === 'image' ? 'Image' : (section.content || section.type || 'Content')}
                    </span>
                  </div>
                )}

                <div
                  className="absolute inset-0 bg-gray-800 bg-opacity-60 transition-all duration-1000"
                  style={{ width: `${segmentProgress}%` }}
                />

                {isCompleted && (
                  <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-orange-400 text-white text-center py-2 px-2 rounded-b-xl -mt-2 relative z-10">
                <div className="text-sm font-medium truncate">{section.type === 'image' ? 'Image' : section.content}</div>
                <div className="text-sm font-bold">{section.duration}s</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
